import React, { useState, useMemo, useCallback } from 'react';
import { rasService } from '../services/rasService';
import { LogRecord, QueryParams, ApiResponse } from '../types/api';
import StableSelect from './StableSelect';
import UncontrolledInput from './UncontrolledInput';
import { debugLog, logActiveElement } from '../utils/debug';

// Column definitions for display and sorting (advanced filter columns)
const AVAILABLE_COLUMNS = {
  id: { label: 'ID', type: 'numeric' },
  record_id: { label: 'Record ID', type: 'text' },
  status: { label: 'Status', type: 'text' },
  created_at: { label: 'Created At', type: 'date' },
  details_id: { label: 'Details ID', type: 'numeric' },
  ex_origin: { label: 'Ex Origin', type: 'text' },
  ex_date: { label: 'Ex Date', type: 'date' },
  ex_meeting_id: { label: 'Ex Meeting ID', type: 'text' },
  ex_track_id: { label: 'Ex Track ID', type: 'text' },
  ex_event_id: { label: 'Ex Event ID', type: 'text' },
  ex_horse_id: { label: 'Ex Horse ID', type: 'text' },
  g1_race_id: { label: 'G1 Race ID', type: 'numeric' },
  g1_horse_id: { label: 'G1 Horse ID', type: 'numeric' },
  g1_runner_id: { label: 'G1 Runner ID', type: 'numeric' },
  g1_horse_match: { label: 'G1 Horse Match', type: 'text' },
  g1_race_status: { label: 'G1 Race Status', type: 'text' },
  g1_runner_status: { label: 'G1 Runner Status', type: 'text' },
  g1_horse_update: { label: 'G1 Horse Update', type: 'text' },
  unsuccessful_only: { label: 'Unsuccessful Only', type: 'boolean' }
} as const;

type ColumnKey = keyof typeof AVAILABLE_COLUMNS;
type ColumnType = typeof AVAILABLE_COLUMNS[ColumnKey]['type'];

const OPERATORS_BY_TYPE: Record<ColumnType, Array<{value: string, label: string}>> = {
  text: [
    { value: '=', label: 'Equals' },
    { value: '!=', label: 'Not equal (!-)' },
    { value: 'LIKE', label: 'Contains (LIKE)' },
    // { value: 'ILIKE', label: 'Contains case-insensitive (ILIKE)' },
  ],
  numeric: [
    { value: '=', label: 'Equals' },
    { value: '!=', label: 'Not equal (!=)' },
    { value: '>', label: 'Greater than (>)' },
    { value: '>=', label: 'Greater than or equal (>=)' },
    { value: '<', label: 'Less than (<)' },
    { value: '<=', label: 'Less than or equal (<=)' },
  ],
  date: [
    { value: '=', label: 'Equals' },
    { value: '!=', label: 'Not equal (!=)' },
    { value: '>', label: 'After (>)' },
    { value: '>=', label: 'After or equal (>=)' },
    { value: '<', label: 'Before (<)' },
    { value: '<=', label: 'Before or equal (<=)' },
  ],
  boolean: [
    { value: '=', label: 'Equals' },
  ],
};

interface FilterRow {
  id: string;
  column: ColumnKey;
  operator: string;
  value: string;
}

interface SortConfig {
  field: ColumnKey;
  direction: 'asc' | 'desc' | null;
}

const LogQueryPanel: React.FC = () => {
  // State for advanced filters
  // Advanced filter rows (default to status equals filter)
  const [filters, setFilters] = useState<FilterRow[]>([
    { id: crypto.randomUUID(), column: 'status', operator: '=', value: '' }
  ]);
  const [logs, setLogs] = useState<ApiResponse<LogRecord[]> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ field: 'id', direction: null });
  const [limit, setLimit] = useState(50);

  const quickFilters = [
    { label: 'Success', value: 'success' },
    { label: 'Failed', value: 'error' },
    { label: 'No Status', value: '' }
  ];


  const columnOptions = useMemo(() => 
    Object.entries(AVAILABLE_COLUMNS).map(([key, config]) => ({
      value: key,
      label: config.label
    })), []
  );

  const getOperatorOptions = useCallback((columnKey: ColumnKey) => {
    const columnType = AVAILABLE_COLUMNS[columnKey]?.type || 'text';
    return OPERATORS_BY_TYPE[columnType] || OPERATORS_BY_TYPE.text;
  }, []);

  const addFilter = useCallback(() => {
    debugLog('LogQueryPanel', 'Adding new filter');
    // New filter defaults to status equals
    setFilters(prev => [...prev, {
      id: crypto.randomUUID(),
      column: 'status',
      operator: '=',
      value: ''
    }]);
  }, []);

  const removeFilter = useCallback((filterId: string) => {
    debugLog('LogQueryPanel', 'Removing filter', filterId);
    setFilters(prev => {
      const remaining = prev.filter(f => f.id !== filterId);
      
      // If this would remove the last filter, replace it with a default empty filter
      if (remaining.length === 0) {
        // Ensure at least one default status filter
        return [{ id: crypto.randomUUID(), column: 'status', operator: '=', value: '' }];
      }
      return remaining;
    });
  }, []);

  const updateFilter = useCallback((filterId: string, field: keyof FilterRow, value: string) => {
    debugLog('LogQueryPanel', 'Updating filter', { filterId, field, value });
    logActiveElement('LogQueryPanel', 'Before filter update');
    
    setFilters(prev => prev.map(filter => {
      if (filter.id === filterId) {
        const updated = { ...filter, [field]: value };
        // Reset operator when column changes
        if (field === 'column') {
          const newColumnKey = value as ColumnKey;
          const availableOperators = getOperatorOptions(newColumnKey);
          updated.operator = availableOperators[0]?.value || '';
        }
        
        
        return updated;
      }
      return filter;
    }));
  }, [getOperatorOptions]);

  const clearAllFilters = () => {
    // Reset to default status equals filter
    setFilters([{ id: crypto.randomUUID(), column: 'status', operator: '=', value: '' }]);
    setLogs(null);
    setError(null);
  };

  const handleSearch = async () => {
    debugLog('LogQueryPanel', 'Handling search', { filters, limit });
    setLoading(true);
    setError(null);
    
    try {
      const queryParams: QueryParams = { limit };
      
      // Add advanced filters
      filters.forEach(filter => {
        if (filter.value.trim()) {
          // Handle special parameters like unsuccessful_only
          if (filter.column === 'unsuccessful_only') {
            // For unsuccessful_only, treat as boolean parameter
            const boolValue = filter.value.trim().toLowerCase();
            if (boolValue === 'true' || boolValue === '1' || boolValue === 'yes') {
              queryParams.unsuccessful_only = true;
            }
            return; // Don't add as regular column filter
          }
          
          // For '=' operator (empty string), use just the column name as key
          // For other operators, append the operator to the column name
        // Use column key directly for '=' operator, otherwise append operator to key
        const paramKey = filter.operator === '=' ? filter.column : `${filter.column}${filter.operator}`;
          let paramValue: string = filter.value.trim();
          
          // Handle LIKE operators - wrap with wildcards
          if (filter.operator === 'LIKE' || filter.operator === 'ILIKE') {
            paramValue = `%${paramValue}%`;
          }
          
          (queryParams as any)[paramKey] = paramValue;
        }
      });
      
      debugLog('LogQueryPanel', 'Query params built', queryParams);
      const result = await rasService.queryLogs(queryParams);
      setLogs(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to query logs');
      setLogs(null);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickFilter = (column: ColumnKey, value: string) => {
    debugLog('LogQueryPanel', 'Applying quick filter', { column, value });
    
    // Clear all existing filters and add only the selected filter
    const quickFilter: FilterRow = {
      id: crypto.randomUUID(),
      column: column,
      operator: '=',
      value: value
    };
    
    setFilters([quickFilter]);
  };

  const handleSort = (field: ColumnKey) => {
    setSortConfig(prev => {
      if (prev.field !== field) {
        return { field, direction: 'asc' };
      }
      
      switch (prev.direction) {
        case null:
          return { field, direction: 'asc' };
        case 'asc':
          return { field, direction: 'desc' };
        case 'desc':
          return { field, direction: null };
        default:
          return { field, direction: 'asc' };
      }
    });
  };

  const getSortIcon = (field: ColumnKey) => {
    if (sortConfig.field !== field) return '↕';
    switch (sortConfig.direction) {
      case 'asc': return '↑';
      case 'desc': return '↓';
      default: return '↕';
    }
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  const formatCellValue = (value: any): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'success': return '#28a745';
      case 'failed': 
      case 'error': return '#dc3545';
      case 'processing': return '#007bff';
      case 'pending': return '#ffc107';
      default: return '#6c757d';
    }
  };

  // Sort the logs data
  const sortedLogs = useMemo(() => {
    if (!logs?.data || !sortConfig.direction) return logs?.data || [];
    
    const sorted = [...logs.data].sort((a, b) => {
      // Skip sorting for unsuccessful_only column as it's not part of LogRecord
      if (sortConfig.field === 'unsuccessful_only') {
        return 0; // No sorting for this special column
      }
      
      const aValue = a[sortConfig.field as keyof LogRecord];
      const bValue = b[sortConfig.field as keyof LogRecord];
      
      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
      if (bValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
      
      // Get column type for proper sorting
      const columnType = AVAILABLE_COLUMNS[sortConfig.field].type;
      
      let comparison = 0;
      if (columnType === 'numeric') {
        comparison = Number(aValue) - Number(bValue);
      } else if (columnType === 'date') {
        comparison = new Date(aValue).getTime() - new Date(bValue).getTime();
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }
      
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [logs?.data, sortConfig]);


  return (
    <div className="panel-content">
      {/* <h2 className="panel-title">Log Query</h2> */}

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Quick Filters */}
      <div style={{ marginBottom: '20px' }}>
        <h3>Quick Filters</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '15px' }}>
          {quickFilters.map(filter => (
            <button
              key={filter.value}
              onClick={() => handleQuickFilter('status', filter.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              {filter.label}
            </button>
          ))}
          <button
            onClick={() => handleQuickFilter('unsuccessful_only', 'true')}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: '#f8f9fa',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Unsuccessful Only
          </button>
          <button
            onClick={clearAllFilters}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: '#f8f9fa',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Advanced Filters */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h3>Advanced Filters</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              onClick={addFilter} 
              style={{ 
                padding: '6px 12px', 
                border: '1px solid #007bff', 
                borderRadius: '4px', 
                backgroundColor: '#007bff', 
                color: 'white', 
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              + Add Filter
            </button>
            <button 
              onClick={clearAllFilters} 
              style={{ 
                padding: '6px 12px', 
                border: '1px solid #6c757d', 
                borderRadius: '4px', 
                backgroundColor: '#6c757d', 
                color: 'white', 
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Filter Rows */}
        {filters.map((filter, index) => (
          <div key={filter.id} style={{ 
            display: 'flex', 
            gap: '10px', 
            alignItems: 'center', 
            marginBottom: '10px', 
            padding: '10px', 
            border: '1px solid #e0e0e0', 
            borderRadius: '4px', 
            backgroundColor: '#f9f9f9' 
          }}>
            <span style={{ minWidth: '30px', color: '#666', fontSize: '12px' }}>#{index + 1}</span>
            
            <div style={{ minWidth: '200px' }}>
              <StableSelect
                id={`log-column-${filter.id}`}
                value={filter.column}
                onChange={(value) => updateFilter(filter.id, 'column', value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
              >
                <option value="">Select column...</option>
                {columnOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </StableSelect>
            </div>

            <div style={{ minWidth: '150px' }}>
              <StableSelect
                id={`log-operator-${filter.id}`}
                value={filter.operator}
                onChange={(value) => updateFilter(filter.id, 'operator', value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
              >
                <option value="">Select operator...</option>
                {getOperatorOptions(filter.column).map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </StableSelect>
            </div>

            <div style={{ flex: 1, minWidth: '200px', maxWidth: 'calc(100% - 250px)' }}>
              {filter.column === 'unsuccessful_only' ? (
                <StableSelect
                  id={`log-value-${filter.id}`}
                  value={filter.value}
                  onChange={(value) => updateFilter(filter.id, 'value', value)}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                >
                  <option value="">Select value...</option>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </StableSelect>
              ) : (
                <UncontrolledInput
                  id={`log-value-${filter.id}`}
                  type={AVAILABLE_COLUMNS[filter.column]?.type === 'date' ? 'datetime-local' : AVAILABLE_COLUMNS[filter.column]?.type === 'numeric' ? 'number' : 'text'}
                  value={filter.value}
                  onChange={(value) => updateFilter(filter.id, 'value', value)}
                  placeholder={filter.operator === 'LIKE' || filter.operator === 'ILIKE' ? 'Partial match' : `Enter ${AVAILABLE_COLUMNS[filter.column]?.label.toLowerCase() || 'value'}...`}
                  debounceMs={150}
                  style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px' }}
                />
              )}
            </div>

            <div style={{ flexShrink: 0, marginLeft: '16px' }}>
              <button
                onClick={() => removeFilter(filter.id)}
                style={{ 
                  padding: '6px 10px', 
                  border: '1px solid #dc3545', 
                  borderRadius: '4px', 
                  backgroundColor: '#dc3545', 
                  color: 'white', 
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Search Controls */}
      <div style={{ display: 'flex', gap: '15px', alignItems: 'center', marginBottom: '20px', padding: '15px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label htmlFor="log-limit" style={{ marginRight: '8px', fontWeight: 'bold' }}>Limit:</label>
          <select
            id="log-limit"
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value) || 0)}
            style={{ padding: '6px', border: '1px solid #ddd', borderRadius: '4px' }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
        </div>
        <button
          onClick={handleSearch}
          disabled={loading}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 'bold',
            opacity: loading ? 0.6 : 1,
          }}
        >
          {loading ? (
            <>
              <span className="loading-spinner" style={{ marginRight: '8px' }} />
              Searching...
            </>
          ) : (
            'Search Logs'
          )}
        </button>
      </div>



      {/* Results */}
      {logs && (
        <div style={{ border: '1px solid #e9ecef', borderRadius: '8px', backgroundColor: '#ffffff', overflow: 'hidden' }}>
          <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #e9ecef' }}>
            <h3 style={{ margin: 0 }}>Search Results</h3>
            <p style={{ margin: '5px 0 0 0', color: '#6c757d' }}>
              Found {logs.count || 0} records {logs.data && `(showing ${logs.data.length})`}
              {sortConfig.direction && (
                <span style={{ marginLeft: '10px' }}>
                  | Sorted by {AVAILABLE_COLUMNS[sortConfig.field].label} ({sortConfig.direction})
                </span>
              )}
            </p>
          </div>

          {logs.data && logs.data.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1800px' }}>
                <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                  <tr>
                    {Object.entries(AVAILABLE_COLUMNS).map(([key, config]) => (
                      <th
                        key={key}
                        onClick={() => handleSort(key as ColumnKey)}
                        style={{
                          padding: '12px 8px',
                          textAlign: 'left',
                          borderBottom: '1px solid #e9ecef',
                          cursor: 'pointer',
                          userSelect: 'none',
                          backgroundColor: sortConfig.field === key ? '#e3f2fd' : '#f8f9fa',
                          minWidth: key === 'record_id' ? '120px' : '100px',
                          fontSize: '12px',
                          fontWeight: '600'
                        }}
                      >
                        {config.label} {getSortIcon(key as ColumnKey)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedLogs.map((log, index) => (
                    <tr key={log.id} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa' }}>
                      {Object.entries(AVAILABLE_COLUMNS).map(([key, config]) => {
                        // Skip unsuccessful_only column in table display as it's not part of LogRecord
                        if (key === 'unsuccessful_only') {
                          return (
                            <td key={key} style={{ padding: '8px', borderBottom: '1px solid #e9ecef', fontSize: '12px' }}>
                              N/A
                            </td>
                          );
                        }
                        
                        const value = log[key as keyof LogRecord];
                        const formattedValue = key === 'created_at' || key === 'ex_date' ? formatDate(value as string) : formatCellValue(value);
                        
                        return (
                          <td 
                            key={key}
                            style={{ 
                              padding: '8px', 
                              borderBottom: '1px solid #e9ecef', 
                              fontSize: key === 'record_id' || key.startsWith('ex_') || key.startsWith('g1_') ? '11px' : '12px',
                              fontFamily: key === 'record_id' ? 'monospace' : 'inherit',
                              maxWidth: key === 'record_id' ? '120px' : key.startsWith('g1_horse_match') ? '120px' : '100px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={formattedValue}
                          >
                            {key === 'status' ? (
                              <span style={{
                                color: getStatusColor(value as string),
                                fontWeight: '500',
                                fontSize: '11px'
                              }}>
                                {formattedValue || 'N/A'}
                              </span>
                            ) : (
                              formattedValue
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6c757d' }}>
              No logs found matching your search criteria
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LogQueryPanel;